import Dexie, { Table, Transaction } from 'dexie'
import {
	ReworkAssignmentCache,
	TaskAssignmentCache,
	WorkItemAssignmentCache,
	WorkItemAttachmentBlobCache,
} from '../Cache/ForemanCache'
import { Employee } from '../Employee/Employee'
import { ReworkTask, WorkItemTask } from '../Work/WorkItemTask'
import { WorkItem } from '../Work/WorkItem'
import { Constraint } from '../Work/Constraint'
import { RequestBacklog } from '../Work/Request'
import { DirectReportAttendance } from '../Employee/Attendance'
import { Pallet, PalletItem } from '../Material/Order'
import { TimeSheet } from '../Reports/ShiftEnd/TimeSheet'
import { Activity } from '../Reports/ShiftEnd/Activity'
import { UpdatedAttachment } from '../Upload/Attachment'
import { WorkItemAttachmentCache } from '../Cache/ForemanCache'
import { OfflineMutation } from '../Core/OfflineMutation'
import { MutationSnapshot } from '../Cache/MutationSnapshot'
import { WorkItemStatus } from '../Work/WorkItemStatus'
import { ChangeType } from '../Cache/MutationSnapshot'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { mergeConsecutiveDuplicates, splitTimesheet } from '../../utils/TimesheetUtils'
import {
	trackEntityUpdate,
	trackEntityInsert,
	trackEntityDelete,
	trackAssignmentChange,
	trackTimeDelete,
} from '../../utils/MutationSnapshotUtils'
import { useTokenClaims } from '../../security/useTokenClaims'
dayjs.extend(utc)
dayjs.extend(timezone)
export class ForemanDB extends Dexie {
	private currentUserId = 1
	private hooksEnabled = true
	private lastLogId = ""
	cacheInfo!: Table<{ userId: number; createdAt: Date }, number>
	employee!: Table<Employee, number>
	directReports!: Table<Employee, number>
	workItems!: Table<WorkItem, number>
	tasks!: Table<WorkItemTask, number>
	reworks!: Table<ReworkTask, number>
	constraints!: Table<Constraint, [number, number]>
	requests!: Table<RequestBacklog, number>
	qcWorkers!: Table<Employee, number>
	workItemAssignments!: Table<WorkItemAssignmentCache, [number, number]>
	taskAssignments!: Table<TaskAssignmentCache, [number, number]>
	reworkAssignments!: Table<ReworkAssignmentCache, [number, number]>
	attendance!: Table<DirectReportAttendance, number>
	pallets!: Table<Pallet, number>
	palletItems!: Table<PalletItem, [number, 'p' | 'u']>
	timesheets!: Table<TimeSheet, number>
	timeRecords!: Table<Activity, number>
	workItemAttachments!: Table<WorkItemAttachmentCache, number>
	workItemAttachmentBlobs!: Table<WorkItemAttachmentBlobCache, number>
	mutations!: Table<OfflineMutation, number>
	mutationSnapshot!: Table<MutationSnapshot, number>
	consentForms!: Table<
		{ workerId: number; blob: Blob; type: 'DayStartConsent' | 'DayEndConsent' },
		[number, string]
	>
	constraintAttachments!: Table<{ constraintId: number; attachment: Blob }, number>
	syncState!: Table<{ id: number; syncInProgress: boolean }, number>
	constructor() {
		super('ForemanDB')

		this.version(1).stores({
			cacheInfo: 'userId',
			employee: 'id',
			directReports: 'id',
			workItems: 'id',
			tasks: 'id',
			reworks: 'id',
			constraints: '[id+workItemId], id, workItemId',
			requests: 'id',
			qcWorkers: 'id',
			attendance: 'workerId',
			workItemAssignments: '[workerId+workItemId], workerId, workItemId',
			taskAssignments: '[workerId+workItemTaskId], workerId, workItemTaskId',
			reworkAssignments: '[workerId+reworkId], workerId, reworkId',
			pallets: 'palletId',
			palletItems: '[palletItemId+itemType], palletItemId, itemType',
			timesheets: 'id',
			timeRecords: 'id',
			workItemAttachments: 'id',
			workItemAttachmentBlobs: 'id',
			mutations: 'id++',
			mutationSnapshot: 'userId',
			consentForms: '[workerId+type], workerId, type',
			constraintAttachments: 'constraintId',
			syncState: 'id',
		})

		// Set up automatic mutation tracking hooks
		this.setupMutationTrackingHooks()
		this.setCurrentUserId(useTokenClaims().id)
	}

	/**
	 * Set the current user ID for mutation tracking
	 */
	public setCurrentUserId(userId: number): void {
		this.currentUserId = userId
	}

	public setHooksEnabled(enabled: boolean): void {
		this.hooksEnabled = enabled
	}

	public async setSyncInProgress(inProgress: boolean): Promise<void> {
		await this.syncState.put({ id: 1, syncInProgress: inProgress })
	}

	/**
	 * Set up hooks for automatic mutation tracking
	 */
	private setupMutationTrackingHooks(): void {
		// Hook for directReports table
		this.directReports.hook.updating.subscribe(async (modifications, primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			const updatedEntity = { ...obj, ...modifications }
			await trackEntityUpdate(this, 'directReports', updatedEntity, this.currentUserId)
		})

		this.directReports.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'directReports', obj, this.currentUserId)
		})

		this.directReports.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityDelete(this, 'directReports', obj, this.currentUserId)
		})

		// Hook for workItems table
		this.workItems.hook.updating.subscribe(async (modifications, primKey, obj, transaction) => {
			if (!this.hooksEnabled) return

			const updatedEntity = { ...obj, ...modifications }
			await trackEntityUpdate(this, 'workItems', updatedEntity, this.currentUserId)
		})

		this.workItems.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'workItems', obj, this.currentUserId)
		})

		this.workItems.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityDelete(this, 'workItems', obj, this.currentUserId)
		})

		// Hook for tasks table
		this.tasks.hook.updating.subscribe(async (modifications, primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			const updatedEntity = { ...obj, ...modifications }
			await trackEntityUpdate(this, 'tasks', updatedEntity, this.currentUserId)
		})

		this.tasks.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'tasks', obj, this.currentUserId)
		})

		this.tasks.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityDelete(this, 'tasks', obj, this.currentUserId)
		})

		// Hook for reworks table
		this.reworks.hook.updating.subscribe(async (modifications, primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			const updatedEntity = { ...obj, ...modifications }
			await trackEntityUpdate(this, 'reworks', updatedEntity, this.currentUserId)
		})

		this.reworks.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'reworks', obj, this.currentUserId)
		})

		this.reworks.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityDelete(this, 'reworks', obj, this.currentUserId)
		})

		// Hook for constraints table
		this.constraints.hook.updating.subscribe(async (modifications, primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			const updatedEntity = { ...obj, ...modifications }
			await trackEntityUpdate(this, 'constraints', updatedEntity, this.currentUserId)
		})

		this.constraints.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'constraints', obj, this.currentUserId)
		})

		this.constraints.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityDelete(this, 'constraints', obj, this.currentUserId)
		})

		// Hook for requests table
		this.requests.hook.updating.subscribe(async (modifications, primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			const updatedEntity = { ...obj, ...modifications }
			await trackEntityUpdate(this, 'requests', updatedEntity, this.currentUserId)
		})

		this.requests.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'requests', obj, this.currentUserId)
		})

		this.requests.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityDelete(this, 'requests', obj, this.currentUserId)
		})

		// Hook for workItemAssignments table
		this.workItemAssignments.hook.updating.subscribe(
			async (modifications, primKey, obj, transaction) => {
				if (!this.hooksEnabled) return
				await trackAssignmentChange(
					this,
					'workItemAssignments',
					obj,
					ChangeType.INSERTED,
					this.currentUserId,
				)
			},
		)

		this.workItemAssignments.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackAssignmentChange(
				this,
				'workItemAssignments',
				obj,
				ChangeType.INSERTED,
				this.currentUserId,
			)
		})

		this.workItemAssignments.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackAssignmentChange(
				this,
				'workItemAssignments',
				obj,
				ChangeType.DELETED,
				this.currentUserId,
			)
		})

		// Hook for taskAssignments table
		this.taskAssignments.hook.updating.subscribe(
			async (modifications, primKey, obj, transaction) => {
				if (!this.hooksEnabled) return
				await trackAssignmentChange(
					this,
					'taskAssignments',
					obj,
					ChangeType.INSERTED,
					this.currentUserId,
				)
			},
		)

		this.taskAssignments.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackAssignmentChange(
				this,
				'taskAssignments',
				obj,
				ChangeType.INSERTED,
				this.currentUserId,
			)
		})

		this.taskAssignments.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackAssignmentChange(
				this,
				'taskAssignments',
				obj,
				ChangeType.DELETED,
				this.currentUserId,
			)
		})

		// Hook for reworkAssignments table
		this.reworkAssignments.hook.updating.subscribe(
			async (modifications, primKey, obj, transaction) => {
				if (!this.hooksEnabled) return
				await trackAssignmentChange(
					this,
					'reworkAssignments',
					obj,
					ChangeType.INSERTED,
					this.currentUserId,
				)
			},
		)

		this.reworkAssignments.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackAssignmentChange(
				this,
				'reworkAssignments',
				obj,
				ChangeType.INSERTED,
				this.currentUserId,
			)
		})

		this.reworkAssignments.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackAssignmentChange(
				this,
				'reworkAssignments',
				obj,
				ChangeType.DELETED,
				this.currentUserId,
			)
		})

		// Hook for attendance table
		this.attendance.hook.updating.subscribe((modifications, primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			const updatedEntity = { ...obj, ...modifications }

			const previousStatus = obj.status
			const newStatus = modifications.status

			if (previousStatus === null && newStatus !== null) {
				// Status was null and now has a value - this is an INSERT
				trackEntityInsert(this, 'attendance', updatedEntity, this.currentUserId)
			} else {
				// Status was not null and being updated - this is an UPDATE
				trackEntityUpdate(this, 'attendance', updatedEntity, this.currentUserId)
			}
		})

		// Hook for pallets table
		this.pallets.hook.updating.subscribe(async (modifications, primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			const updatedEntity = { ...obj, ...modifications }
			await trackEntityUpdate(this, 'pallets', updatedEntity, this.currentUserId)
		})

		this.pallets.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'pallets', obj, this.currentUserId)
		})

		this.pallets.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityDelete(this, 'pallets', obj, this.currentUserId)
		})

		// Hook for palletItems table
		this.palletItems.hook.updating.subscribe(async (modifications, primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			const updatedEntity = { ...obj, ...modifications }
			await trackEntityUpdate(this, 'palletItems', updatedEntity, this.currentUserId)
		})

		this.palletItems.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'palletItems', obj, this.currentUserId)
		})

		this.palletItems.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityDelete(this, 'palletItems', obj, this.currentUserId)
		})

		// Hook for timesheets table
		this.timesheets.hook.updating.subscribe(async (modifications, primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			const updatedEntity = { ...obj, ...modifications }
			await trackEntityUpdate(this, 'timesheets', updatedEntity, this.currentUserId)
		})

		this.timesheets.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'timesheets', obj, this.currentUserId)
		})

		this.timesheets.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackTimeDelete(this, 'timesheets', obj, this.currentUserId)
		})

		// Hook for timeRecords table
		this.timeRecords.hook.updating.subscribe(async (modifications, primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			const updatedEntity = { ...obj, ...modifications }
			await trackEntityUpdate(this, 'timeRecords', updatedEntity, this.currentUserId)
		})

		this.timeRecords.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'timeRecords', obj, this.currentUserId)
		})

		this.timeRecords.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackTimeDelete(this, 'timeRecords', obj, this.currentUserId)
		})

		// Hook for workItemAttachments table
		this.workItemAttachments.hook.updating.subscribe(
			async (modifications, primKey, obj, transaction) => {
				if (!this.hooksEnabled) return
				const updatedEntity = { ...obj, ...modifications }
				await trackEntityUpdate(this, 'attachments', updatedEntity, this.currentUserId)
			},
		)

		this.workItemAttachments.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'attachments', obj, this.currentUserId)
		})

		this.workItemAttachments.hook.deleting.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityDelete(this, 'attachments', obj, this.currentUserId)
		})

		// Hook for consentForms table
		this.consentForms.hook.creating.subscribe(async (primKey, obj, transaction) => {
			if (!this.hooksEnabled) return
			await trackEntityInsert(this, 'consentForms', obj, this.currentUserId)
		})
	}

	// MANUAL CLEANUP METHODS FOR TASK AND REWORK ASSIGNMENTS
	public async cleanupTaskAssignment(workerId: number, workItemTaskId: number, tx: Transaction) {
		await this.cleanupActiveTaskId(workerId, workItemTaskId, tx)
		await this.checkAndRemoveWorkItemAssignment(workerId, workItemTaskId, 'task', tx)
		await this.checkAndUpdateTaskStatus(workItemTaskId, tx)
	}

	public async cleanupReworkAssignment(workerId: number, reworkId: number, tx: Transaction) {
		await this.cleanupActiveReworkId(workerId, reworkId, tx)
		await this.checkAndRemoveWorkItemAssignment(workerId, reworkId, 'rework', tx)
		await this.checkAndUpdateReworkStatus(reworkId, tx)
	}

	// DELETE ActiveTaskId if no other task is assigned to this worker
	private async cleanupActiveTaskId(workerId: number, workItemTaskId: number, tx: Transaction) {
		try {
			const worker = await tx.table('directReports').get(workerId)
			if (worker && worker.activeTaskId === workItemTaskId) {
				await this.addTimeRecord(tx, workerId, false)
				await tx.table('directReports').update(workerId, { activeTaskId: undefined })
			}
		} catch (error) {
			console.error('Error cleaning up activeTaskId:', error)
		}
	}

	// DELETE ActiveReworkId if no other rework is assigned to this worker
	private async cleanupActiveReworkId(workerId: number, reworkId: number, tx: Transaction) {
		try {
			const worker = await tx.table('directReports').get(workerId)
			if (worker && worker.activeReworkId === reworkId) {
				await this.addTimeRecord(tx, workerId, false)
				await tx.table('directReports').update(workerId, { activeReworkId: undefined })
			}
		} catch (error) {
			console.error('Error cleaning up activeReworkId:', error)
		}
	}

	// DELETE WorkItemAssignment if no other task or rework is assigned to this worker
	private async checkAndRemoveWorkItemAssignment(
		workerId: number,
		assignmentId: number,
		assignmentType: 'task' | 'rework',
		tx: Transaction,
	) {
		try {
			let workItemId: number | undefined
			if (assignmentType === 'task') {
				const task = await tx.table('tasks').get(assignmentId)
				workItemId = task?.workItemId
			} else {
				const rework = await tx.table('reworks').get(assignmentId)
				workItemId = rework?.workItemId
			}

			if (!workItemId) {
				return
			}

			const remainingTaskAssignments = await tx
				.table('taskAssignments')
				.filter((t) => t.workerId === workerId)
				.toArray()

			const workItemTasks = await tx
				.table('tasks')
				.filter((t) => t.workItemId === workItemId)
				.toArray()

			const hasRemainingTaskAssignments = remainingTaskAssignments.some((taskAssignment) =>
				workItemTasks.some((task) => task.id === taskAssignment.workItemTaskId),
			)

			const remainingReworkAssignments = await tx
				.table('reworkAssignments')
				.filter((r) => r.workerId === workerId)
				.toArray()

			const workItemReworks = await tx
				.table('reworks')
				.filter((r) => r.workItemId === workItemId)
				.toArray()

			const hasRemainingReworkAssignments = remainingReworkAssignments.some((reworkAssignment) =>
				workItemReworks.some((rework) => rework.id === reworkAssignment.reworkId),
			)

			if (!hasRemainingTaskAssignments && !hasRemainingReworkAssignments) {
				await tx
					.table('workItemAssignments')
					.where(['workerId', 'workItemId'])
					.equals([workerId, workItemId])
					.delete()
			}
		} catch (error) {
			console.error('Error checking and removing work item assignment:', error)
		}
	}

	// UPDATE TASK STATUS TO INACTIVE IF NO WORKER HAS IT AS ACTIVE
	private async checkAndUpdateTaskStatus(taskId: number, tx: Transaction) {
		try {
			// Check if any worker still has this task as active
			const workersWithActiveTask = await tx
				.table('directReports')
				.filter((dr) => dr.activeTaskId === taskId)
				.count()

			// If no workers have this task as active, set task status to Inactive
			if (workersWithActiveTask === 0) {
				const task = await tx.table('tasks').get(taskId)
				if (task) {
					await tx.table('tasks').update(taskId, { status: 'Inactive' })
				}
			}
		} catch (error) {
			console.error('Error updating task status:', error)
		}
	}

	// UPDATE REWORK STATUS TO INACTIVE IF NO WORKER HAS IT AS ACTIVE
	private async checkAndUpdateReworkStatus(reworkId: number, tx: Transaction) {
		try {
			// Check if any worker still has this rework as active
			const workersWithActiveRework = await tx
				.table('directReports')
				.filter((dr) => dr.activeReworkId === reworkId)
				.count()

			// If no workers have this rework as active, set rework status to Inactive
			if (workersWithActiveRework === 0) {
				const rework = await tx.table('reworks').get(reworkId)
				if (rework) {
					await tx.table('reworks').update(reworkId, { status: 'Inactive' })
				}
			}
		} catch (error) {
			console.error('Error updating rework status:', error)
		}
	}

	// TRIGGER WORK ITEM STATUS UPDATES
	public async triggerWorkItemStatusUpdate(workItemId: number, tx: Transaction) {
		const workItemTasks = await tx
			.table('tasks')
			.filter((task) => task.workItemId === workItemId)
			.toArray()
		const workItemReworks = await tx
			.table('reworks')
			.filter((rework) => rework.workItemId === workItemId)
			.toArray()

		const hasActiveTasks =
			workItemTasks.length > 0 &&
			(await tx
				.table('directReports')
				.filter(
					(dr) =>
						dr.activeTaskId !== undefined &&
						workItemTasks.some((task) => task.id === dr.activeTaskId),
				)
				.count()) > 0

		const hasActiveReworks =
			workItemReworks.length > 0 &&
			(await tx
				.table('directReports')
				.filter(
					(dr) =>
						dr.activeReworkId !== undefined &&
						workItemReworks.some((rework) => rework.id === dr.activeReworkId),
				)
				.count()) > 0

		const workItem = await tx.table('workItems').get(workItemId)
		if (!workItem) return

		if (workItem.status === 'Constrained') {
			return
		}

		let newStatus: WorkItemStatus
		if (hasActiveTasks || hasActiveReworks) {
			newStatus = 'Active'
		} else {
			const hasConstraints =
				(await tx
					.table('constraints')
					.filter((c) => c.workItemId === workItemId)
					.count()) > 0

			if (hasConstraints) {
				return
			} else {
				if (workItem.percentComplete === 0) {
					newStatus = 'Not Started'
				} else if (Math.abs(workItem.percentComplete - 1.0) < 0.01) {
					newStatus = 'Complete'
				} else {
					newStatus = 'Inactive'
				}
			}
		}

		await tx.table('workItems').update(workItemId, {
			status: newStatus,
		})

		// PHASE B: Climb up the chain and update parent statuses
		await this.updateParentStatuses(workItemId, tx)
	}

	// UPDATE PARENT STATUSES
	private async updateParentStatuses(leafWorkItemId: number, tx: Transaction) {
		let currentId = (await tx.table('workItems').get(leafWorkItemId))?.parentId

		while (currentId !== null && currentId !== undefined) {
			const currentWorkItem = await tx.table('workItems').get(currentId)
			if (!currentWorkItem) break

			if (currentWorkItem.status === 'Constrained') {
				currentId = currentWorkItem.parentId
				continue
			}

			const children = await tx
				.table('workItems')
				.filter((wi) => wi.parentId === currentId)
				.toArray()

			let newParentStatus: WorkItemStatus

			if (children.some((child) => child.status === 'Active')) {
				newParentStatus = 'Active'
			} else if (children.every((child) => child.status === 'Complete')) {
				newParentStatus = 'Complete'
			} else if (children.every((child) => child.status === 'Not Started')) {
				newParentStatus = 'Not Started'
			} else if (children.every((child) => child.status === 'Pending Material')) {
				newParentStatus = 'Pending Material'
			} else if (children.every((child) => child.status === 'Ready')) {
				newParentStatus = 'Ready'
			} else {
				newParentStatus = 'Inactive'
			}

			await tx.table('workItems').update(currentId, {
				status: newParentStatus,
			})

			currentId = currentWorkItem.parentId
		}
	}

	// ADD TIME RECORD
	public async addTimeRecord(
		tx: Transaction,
		workerId: number,
		constraint: boolean,
		comment?: string,
		task?: WorkItemTask | ReworkTask,
	) {
		const timesheet = await tx
			.table('timesheets')
			.filter((t) => t.workerId === workerId && dayjs(t.localDate).isSame(new Date(), 'day'))
			.first()
		const worker = await tx.table('directReports').get(workerId)
		const attendance = await tx
			.table('attendance')
			.filter((a) => a.workerId === workerId)
			.first()

		const newTimesheetId = await this.generateLocalId('timesheets', tx)
		const newTimeRecordId = await this.generateLocalId('timeRecords', tx)
		const newTimeRecord: Activity = {
			id: newTimeRecordId,
			type: constraint ? comment ?? '' : task ? 'WorkItem' : 'Unproductive Time',
			activity: constraint ? 'Constraint' : task ? task.name : 'Unproductive Time',
			startTime: dayjs(new Date().toISOString().replace('Z', '')).toDate(),
			endTime: null,
			comment: '',
			timesheetId: timesheet ? timesheet.id : newTimesheetId,
			workItemId: task ? task.workItemId! : 0,
			hours: 0,
			workItemTaskId: constraint ? undefined : task && 'creditRule' in task ? task.id : undefined,
			reworkId: constraint ? undefined : task && !('creditRule' in task) ? task.id : undefined,
		}
		if (!timesheet) {
			await tx.table('timesheets').add({
				id: newTimesheetId,
				title: `${worker?.firstName} ${worker?.lastName}`,
				confirmed: false,
				workerId: workerId,
				localDate: new Date(),
				signInAt: attendance?.signInAt ?? undefined,
				signOutAt: attendance?.signOutAt ?? undefined,
				activities: [],
			})
			await tx.table('timeRecords').add(newTimeRecord)
		} else {
			let currentTimeRecords = (await tx
				.table('timeRecords')
				.filter((tr) => tr.timesheetId === timesheet.id)
				.toArray()) as Activity[]
			currentTimeRecords = mergeConsecutiveDuplicates(
				splitTimesheet(newTimeRecord, currentTimeRecords),
			)
			await tx.table('timeRecords').bulkPut(currentTimeRecords)
		}
	}

	// GENERATE LOCAL ID FOR CACHED RECORDS
	public async generateLocalId(tableName: string, tx: Transaction): Promise<number> {
		const minId = await tx.table(tableName).orderBy('id').first()
		if (!minId) {
			return -1
		}
		if (minId.id < 0) {
			return minId.id - 1
		}
		return -1
	}

	public async setWorkItemHierarchyConstraint(
		workItemId: number,
		tx: Transaction,
	): Promise<{ taskIds: number[]; reworkIds: number[] }> {
		const currentWorkItem: WorkItem | undefined = await tx.table('workItems').get(workItemId)
		if (!currentWorkItem) return { taskIds: [], reworkIds: [] }
		await tx.table('workItems').update(currentWorkItem.id, {
			status: 'Constrained',
		})
		const workItemIds: number[] = [currentWorkItem.id]
		let childrenWorkItemsIds: number[] = (
			await tx
				.table('workItems')
				.filter((wi) => wi.parentId === currentWorkItem.id)
				.toArray()
		).map((c: WorkItem) => c.id)
		while (childrenWorkItemsIds.length > 0) {
			workItemIds.push(...childrenWorkItemsIds)
			for (const childWorkItem of childrenWorkItemsIds) {
				await tx.table('workItems').update(childWorkItem, {
					status: 'Constrained',
				})
			}
			childrenWorkItemsIds = (
				await tx
					.table('workItems')
					.filter((wi) => childrenWorkItemsIds.some((c) => c === wi.parentId))
					.toArray()
			).map((c: WorkItem) => c.id)
		}
		const taskIds = (
			await tx
				.table('tasks')
				.filter((t: WorkItemTask) => workItemIds.includes(t.workItemId!))
				.toArray()
		).map((t: WorkItemTask) => t.id)
		const reworkIds = (
			await tx
				.table('reworks')
				.filter((r: ReworkTask) => taskIds.includes(r.workItemTaskId!))
				.toArray()
		).map((r: ReworkTask) => r.id)
		return { taskIds, reworkIds }
	}
	public async addConstraint(
		workItemId: number,
		constraintComment: string,
		workerId: number,
		attachment: Blob | null,
		tx: Transaction,
	) {
		const worker = await tx.table('employee').get(workerId)
		if (!worker) return
		const constraint = await tx.table('constraints').add({
			id: await this.generateLocalId('constraints', tx),
			workItemId,
			comments: constraintComment,
			attachments: [],
			reportedByName: `${worker.firstName} ${worker.lastName}`,
			timeReported: dayjs(new Date().toISOString().replace('Z', '')).toDate(),
			workItemName: (await tx.table('workItems').get(workItemId))?.name ?? '',
			timeCleared: undefined,
			reportedById: workerId,
		})
		if (attachment) {
			await tx.table('constraintAttachments').add({
				constraintId: constraint.id,
				attachment,
			})
		}
	}

	public async calculateWorkItem(workItemId: number, tx: Transaction) {
		const currentWorkItem: WorkItem | undefined = await tx.table('workItems').get(workItemId)
		if (!currentWorkItem) return
		const tasks: WorkItemTask[] = await tx
			.table('tasks')
			.filter((t) => t.workItemId === currentWorkItem!.id)
			.toArray()

		const workItemPercentComplete = Number(
			tasks
				.reduce(
					(acc, task) =>
						acc + (task.quantityComplete * task.creditRule) / currentWorkItem!.quantityBudgeted,
					0,
				)
				.toFixed(3),
		)
		const workItemQtyEarned = Number(
			(workItemPercentComplete * currentWorkItem.quantityBudgeted).toFixed(3),
		)
		const workItemStatus = 1 - workItemPercentComplete < 0.01 ? 'Complete' : null
		const burnedHours = Number(tasks.reduce((acc, task) => acc + task.burnedHours, 0).toFixed(3))
		if (workItemStatus === 'Complete') {
			await tx.table('workItemAssignments').where('workItemId').equals(currentWorkItem.id).delete()
			await tx
				.table('taskAssignments')
				.where('workItemTaskId')
				.anyOf(tasks.map((t) => t.id))
				.delete()
			const reworks: ReworkTask[] = await tx
				.table('reworks')
				.filter((r) => tasks.some((t) => t.id === r.workItemTaskId))
				.toArray()
			await tx
				.table('reworkAssignments')
				.where('reworkId')
				.anyOf(reworks.map((r) => r.id))
				.delete()
		}
		await tx.table('workItems').update(currentWorkItem.id, {
			percentComplete: workItemPercentComplete,
			status: workItemStatus ?? currentWorkItem.status,
			quantityEarned: workItemQtyEarned,
			quantityRemaining: Number((currentWorkItem.quantityBudgeted - workItemQtyEarned).toFixed(3)),
			earnedHours: Number((currentWorkItem.budgetedHours * workItemPercentComplete).toFixed(3)),
			burnedHours: burnedHours,
			pf: Number(
				(burnedHours !== 0
					? // Earned hours / Burned hours
					  (currentWorkItem.budgetedHours * workItemPercentComplete) / burnedHours
					: currentWorkItem.budgetedHours * workItemPercentComplete
				).toFixed(3),
			),
		} as WorkItem)
		let parentWorkItem: WorkItem | undefined = await tx
			.table('workItems')
			.get(currentWorkItem.parentId!)
		while (parentWorkItem) {
			const childrenWorkItems: WorkItem[] = await tx
				.table('workItems')
				.filter((wi) => wi.parentId === parentWorkItem!.id)
				.toArray()
			const parentQtyEarned = Number(
				childrenWorkItems.reduce((acc, wi) => acc + wi.quantityEarned, 0).toFixed(3),
			)
			const parentPercentComplete = Number(
				(parentQtyEarned / parentWorkItem.quantityBudgeted).toFixed(3),
			)
			const parentStatus = 1 - parentPercentComplete < 0.01 ? 'Complete' : null
			const parentBurnedHours = Number(
				childrenWorkItems.reduce((acc, wi) => acc + wi.burnedHours, 0).toFixed(3),
			)

			await tx.table('workItems').update(parentWorkItem.id, {
				percentComplete: parentPercentComplete,
				status: parentStatus ?? parentWorkItem.status,
				quantityEarned: parentQtyEarned,
				quantityRemaining: Number((parentWorkItem.quantityBudgeted - parentQtyEarned).toFixed(3)),
				earnedHours: Number((parentWorkItem.budgetedHours * parentPercentComplete).toFixed(3)),
				burnedHours: parentBurnedHours,
				pf: Number(
					(parentBurnedHours !== 0
						? // Earned hours / Burned hours
						  (parentWorkItem.budgetedHours * parentPercentComplete) / parentBurnedHours
						: parentWorkItem.budgetedHours * parentPercentComplete
					).toFixed(3),
				),
			})
			parentWorkItem = (await tx.table('workItems').get(parentWorkItem.parentId!)) as
				| WorkItem
				| undefined
		}
	}

	public async annotateAttachment(
		workItemId: number,
		attachmentId: number,
		newAnnotations: string,
	) {
		await trackEntityUpdate<UpdatedAttachment>(
			this,
			'updatedAttachments',
			{
				workItemId,
				attachmentId,
				newAnnotations,
			},
			this.currentUserId,
		)
	}

	public async getAttachmentsXFDF() {
		const ms = await this.mutationSnapshot
			.where('userId')
			.equals(this.currentUserId)
			.and((snapshot) => snapshot.updatedAttachments.length > 0)
			.first()
		if (ms) {
			return ms.updatedAttachments
		}
		return []
	}

	public setNewLogId(): void {
		this.lastLogId = crypto.randomUUID()
	}

	public getLastLogId(): string {
		return this.lastLogId
	}
}

export const foremanDB = new ForemanDB()
